import { useState } from 'react'

import { useI18N } from '@susu/i18n/client'
import { buildCdnImageUrl } from '@susu/order-list'
import { Icon } from '@susu/ui/components'

import { useHasBootSkeleton } from '../contexts/BootSkeletonContext'
import { useActiveOrders } from '../hooks/useActiveOrders'
import { useSkeletonPhase } from '../hooks/useSkeletonPhase'
import { cn } from '../lib/utils'
import { resolveActiveOrderText } from '../utils/activeOrderCards'
import {
  getActiveOrderProducts,
  hasCustomMadeProduct,
} from '../utils/activeOrders'

import ActiveOrdersSkeleton from './ActiveOrdersSkeleton'

import type { ActiveOrder } from '../api/activeOrdersApi'
import type { EnvironmentWithChina } from '../App'

interface ActiveOrdersProps {
  accountNumber: string
  authToken?: string
  env: EnvironmentWithChina
  isEnabled: boolean
  onOrderClick?: (order: ActiveOrder) => void
}

export default function ActiveOrders({
  accountNumber,
  authToken,
  env,
  isEnabled,
  onOrderClick,
}: Readonly<ActiveOrdersProps>) {
  const { t } = useI18N()
  const {
    data: orders = [],
    isLoading,
    isError,
  } = useActiveOrders({ accountNumber, env, authToken, enabled: isEnabled })
  const hasBootSkeleton = useHasBootSkeleton()
  const { phase, contentClassName } = useSkeletonPhase(isEnabled && isLoading, {
    skipDelay: hasBootSkeleton,
  })

  if (!isEnabled) {
    return null
  }

  const isEmpty = isError || orders.length === 0

  // Loading is checked before the empty case on purpose: `orders` is still
  // empty while the request is in flight, so testing the empty case first would
  // return null and the skeleton would never render.
  if (phase !== 'content') {
    return <ActiveOrdersSkeleton isReserved={phase === 'reserve'} />
  }

  // Settled with nothing to show: the whole section disappears (#6817).
  if (isEmpty) {
    return null
  }

  return (
    <section
      className={cn('flex flex-col gap-16 max-lg:px-16', contentClassName)}
      data-testid="active-orders"
    >
      <h2 className="text-xl leading-xl font-medium text-text tracking-heading">
        {t('activeOrders.title')}
      </h2>
      <div className="flex flex-col gap-16">
        {orders.map((order, index) => (
          <ActiveOrderCard
            key={`${order.order_id}-${index}`}
            order={order}
            onClick={onOrderClick}
          />
        ))}
      </div>
    </section>
  )
}

function ActiveOrderCard({
  order,
  onClick,
}: Readonly<{
  order: ActiveOrder
  onClick?: (order: ActiveOrder) => void
}>) {
  const { t, language } = useI18N()
  const [imageError, setImageError] = useState(false)
  const imageUrl = buildCdnImageUrl(getActiveOrderProducts(order)[0]?.imageUrl)
  const hasMultipleProducts = (order.products?.length ?? 0) > 1
  const isCustomMade = hasCustomMadeProduct(order)
  const { title, subtitle } = resolveActiveOrderText(order, t, language)

  return (
    <button
      type="button"
      onClick={() => onClick?.(order)}
      className="flex w-full items-center rounded-lg border border-border bg-surface p-16 text-left transition-[background-color] hover:bg-surface-hover"
      data-testid="active-order-card"
    >
      <div className="flex min-w-0 flex-1 items-center gap-20">
        <div className="relative flex h-108 w-90 shrink-0 flex-col items-center justify-center">
          {hasMultipleProducts && (
            <div
              aria-hidden
              className="absolute inset-0 -rotate-[5deg] overflow-hidden rounded-md border border-border bg-background-gray-dark"
            />
          )}
          <div className="relative flex h-108 w-90 flex-col items-center justify-center overflow-hidden rounded-md border border-border bg-background-gray-dark p-10">
            {imageUrl && !imageError ? (
              <img
                src={imageUrl}
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => setImageError(true)}
                className="h-full w-full shrink-0 object-contain"
              />
            ) : (
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>{t('activeOrders.noImage')}</title>
                <rect width="40" height="40" rx="4" fill="#E5E5E5" />
                <path
                  d="M12 28L17 23L21 27L26 22L28 24V28H12Z"
                  fill="#C6C6C6"
                />
                <circle cx="24" cy="16" r="3" fill="#C6C6C6" />
              </svg>
            )}
          </div>
          {isCustomMade && (
            <div className="backdrop-blur-sm absolute top-6 left-6 flex h-22 w-22 items-center justify-center rounded-md bg-white-alpha-600">
              <Icon name="custom-made" size={14} />
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2 pb-2 pr-20 [word-break:break-word]">
          <span
            className="text-base leading-base font-medium text-text"
            data-testid="active-order-status"
          >
            {title}
          </span>
          {subtitle && (
            <span
              className="text-sm leading-sm font-light text-text"
              data-testid="active-order-delivery"
            >
              {subtitle}
            </span>
          )}
        </div>
      </div>
      <Icon name="chevron-right" size="medium" />
    </button>
  )
}
